import {useCallback, useMemo} from 'react';

import {dueWithinBounds} from 'client/TeacherDashboard/SkillPaths/dueWindow';
import type {
  DueWithinFilter,
  SkillPathSortValue,
  SkillPathStatusSegment
} from 'client/TeacherDashboard/SkillPaths/types';
import type {SortValue} from 'client/TeacherDashboard/types';
import type {DiagnosticFilterInput, DiagnosticSortInput} from 'gql/types';
import {DiagnosticSortField, DiagnosticStatus, SortOrder} from 'gql/types';

import type {TeacherDiagnosticListFieldsFragment} from '../Management/queries/TeacherDiagnostics.queries.generated';
import {
  useTeacherDiagnosticStatusCountsQuery,
  useTeacherDiagnosticsListQuery
} from '../Management/queries/TeacherDiagnostics.queries.generated';

/** INTERIM: a single capped page until the diagnostics read gains keyset paging for this view. */
const PAGE_SIZE = 50;

/** Stable identity so the group can detect a genuinely new result set. */
const NO_DIAGNOSTICS: TeacherDiagnosticListFieldsFragment[] = [];

const STATUS_ENUM: Record<SkillPathStatusSegment, DiagnosticStatus> = {
  open: DiagnosticStatus.Open,
  closed: DiagnosticStatus.Closed,
  scheduled: DiagnosticStatus.Scheduled
};

export interface SkillPathsDiagnosticsFilters {
  /** Off with the `adaptive_diagnostics` flag: nothing is fetched and every result is empty. */
  skip: boolean;
  classroomId?: string;
  segment: SkillPathStatusSegment;
  search: string;
  /** Subject id, or `'all'`. */
  subject: string;
  dueWithin: DueWithinFilter;
  dueDateStart: string;
  dueDateEnd: string;
  sort: SortValue<SkillPathSortValue>;
}

/** Everything except the status segment — shared by the visible list and the per-status counts. */
type SharedFilters = Omit<SkillPathsDiagnosticsFilters, 'segment' | 'sort' | 'skip'>;

/** The class scope on its own, before any toolbar filter narrows it. */
const buildScopeFilter = (classroomId?: string): DiagnosticFilterInput =>
  classroomId ? {assignedToClassrooms: {eq: true, input: {classroomIds: [classroomId]}}} : {};

export const buildSharedFilter = ({
  classroomId,
  search,
  subject,
  dueWithin,
  dueDateStart,
  dueDateEnd
}: SharedFilters): DiagnosticFilterInput => {
  const filter: DiagnosticFilterInput = buildScopeFilter(classroomId);
  if (search) {
    filter.name = {ilike: `%${search}%`};
  }
  if (subject !== 'all') {
    filter.guide = {subjectId: {eq: subject}};
  }
  // Asked of the recipient groups, not of `minDueDate`/`maxDueDate`: those are Ash aggregates, and
  // filtering on one raises server-side.
  const {start, end} = dueWithinBounds(dueWithin, dueDateStart, dueDateEnd);
  if (start || end) {
    filter.diagnosticSettings = {
      dueDate: {
        ...(start ? {greaterThanOrEqual: start.toISOString()} : {}),
        ...(end ? {lessThanOrEqual: end.toISOString()} : {})
      }
    };
  }
  return filter;
};

const withStatus = (
  filter: DiagnosticFilterInput,
  segment: SkillPathStatusSegment
): DiagnosticFilterInput => ({...filter, status: {eq: STATUS_ENUM[segment]}});

export const buildDiagnosticSort = ({
  field,
  order
}: SortValue<SkillPathSortValue>): DiagnosticSortInput[] => {
  const sortOrder = order === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  if (field === 'name') {
    return [{field: DiagnosticSortField.Name, order: sortOrder}];
  }
  // Diagnostics expose no start-date sort field, so "Start Date" falls back to the due date. The
  // spec's default, "Recently edited", needs a BE timestamp that does not exist yet either.
  return [
    {field: DiagnosticSortField.MinDueDate, order: sortOrder},
    {field: DiagnosticSortField.Name, order: SortOrder.Asc}
  ];
};

export interface SkillPathsDiagnosticsResult {
  diagnostics: TeacherDiagnosticListFieldsFragment[];
  /** Total matching the current status + filters, so the group can offer the rest. */
  total: number;
  counts: Record<SkillPathStatusSegment, number>;
  /** False until the counts land, so the shared control can hold its numbers back. */
  hasCounts: boolean;
  /** Every status is empty under the current filters — drives the onboarding state. */
  isEmptyInEveryStatus: boolean;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}

/**
 * The diagnostics group inside the Skill Paths view: one capped page for the active status plus the
 * three per-status totals the shared segmented control adds to the skill-path counts.
 */
export const useSkillPathsDiagnostics = (
  filters: SkillPathsDiagnosticsFilters
): SkillPathsDiagnosticsResult => {
  const {skip, segment, sort, ...shared} = filters;
  const {classroomId, search, subject, dueWithin, dueDateStart, dueDateEnd} = shared;

  const sharedFilter = useMemo(
    () => buildSharedFilter({classroomId, search, subject, dueWithin, dueDateStart, dueDateEnd}),
    [classroomId, search, subject, dueWithin, dueDateStart, dueDateEnd]
  );

  const {data, loading, error, refetch} = useTeacherDiagnosticsListQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: withStatus(sharedFilter, segment),
      sort: buildDiagnosticSort(sort),
      first: PAGE_SIZE
    }
  });

  // Separate from the list query so paging or re-sorting the visible status doesn't refetch the
  // other two totals.
  const {
    data: countsData,
    loading: countsLoading,
    error: countsError
  } = useTeacherDiagnosticStatusCountsQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    variables: {
      openFilter: withStatus(sharedFilter, 'open'),
      closedFilter: withStatus(sharedFilter, 'closed'),
      scheduledFilter: withStatus(sharedFilter, 'scheduled')
    }
  });

  const handleRefetch = useCallback(() => {
    // Swallow the rejection: the failure is surfaced reactively via `error`.
    void refetch().catch(() => {});
  }, [refetch]);

  const open = countsData?.open?.count ?? 0;
  const closed = countsData?.closed?.count ?? 0;
  const scheduled = countsData?.scheduled?.count ?? 0;
  // Keyed on the numbers, not the response, so the segmented control's labels only rebuild when a
  // total actually moves.
  const counts = useMemo(() => ({open, closed, scheduled}), [open, closed, scheduled]);

  return {
    diagnostics: data?.diagnostics?.results ?? NO_DIAGNOSTICS,
    total: data?.diagnostics?.count ?? 0,
    counts,
    hasCounts: Boolean(countsData),
    isEmptyInEveryStatus: Boolean(countsData) && !countsError && open + closed + scheduled === 0,
    // A changed result set shows the skeleton rather than the previous status's rows. The totals are
    // part of that set: they decide the empty state, so painting before they land risks swapping the
    // whole section out from under the teacher a moment later.
    isLoading: (loading && !data) || (countsLoading && !countsData),
    // Includes the counts query: a silent failure there would report every status tab as 0.
    hasError: Boolean(error) || Boolean(countsError),
    refetch: handleRefetch
  };
};
